// Kinio Node 概覽「UART 接著」那一格（SPEC #vms-web-s5-19-2 NODE-06；VMS UX 評審第十一輪 R11-P1-2，第 103 列）。
//   ① 對到看得到的設備：狀態點＋名字＋在線 / 離線（跟設備清單同一份）＋〔開它的頁面〕；接線時取的名字不同時小字寫出來
//   ② 只有 label：寫 label＋小字「沒有對到你看得到的 Kinio 設備…」，不給連結
//   ③ 沒回報：「還沒設定接了哪台設備」＋怎麼設；韌體低於 1.0.56 時看韌體分頁同一份 OTA 狀態：更新通道真的有 ≥ 1.0.56 才叫人升級〔看韌體〕，
//      沒有就寫「支援的版本還沒發佈到更新通道」、不給做不到的動作（第十二輪 B12-03）
//   ④ 輸出時間：24 小時內「N 分鐘前有輸出」、超過寫很久沒有輸出（警示色）、Node 離線寫離線前最後收到、沒有 rx_at 寫還沒收到
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { VmsDevice } from '../../lib/devices';
import { relative, stamp } from '../../lib/timefmt';
import { useTzResolver } from '../../lib/useTz';
import { TTL_DUT_MIN_FW, fwAtLeast, rxState, ttlLinked, uartUpgrade } from './ttlTarget';
import { useFwStatus } from './DeviceTabs';

export function NodeUart({ dev, tree, onFirmware }: { dev: VmsDevice; tree: VmsDevice[]; onFirmware(): void }) {
  const { t } = useTranslation();
  const tz = useTzResolver()(dev.homeId);
  const tt = dev.ttlTarget;
  const old = tt === null && fwAtLeast(dev.firmware, TTL_DUT_MIN_FW) === false;
  // 有沒有新版：跟韌體分頁、狀態列「（最新）」同一份資料（平台 OTA 狀態）
  const fw = useFwStatus(dev, old);
  if (tt === undefined) return null;
  if (tt === null) {
    const up = old ? uartUpgrade(fw.data, fw.isError) : null;
    return (
      <div className="nuart" data-testid="nuart" data-nuart="unset">
        <span>{t('nuart.unset')}</span>
        <div className="muted small" data-testid="nuart-howto">
          {t('nuart.howto')} <code>fac_set ttl_dut &lt;{t('nuart.howto_name')}&gt; [&lt;AID&gt;|-] [&lt;{t('nuart.howto_model')}&gt;]</code>
        </div>
        {up?.kind === 'available' && (
          <div className="small warn-text" data-testid="nuart-old-fw" data-upgrade="available">
            {t('nuart.old_fw', { v: dev.firmware, min: TTL_DUT_MIN_FW, next: up.version })}{' '}
            <button type="button" className="link-btn" onClick={onFirmware}>
              {t('nuart.see_fw')}
            </button>
          </div>
        )}
        {up?.kind === 'unreleased' && (
          <div className="small muted" data-testid="nuart-old-fw" data-upgrade="unreleased">
            {t('nuart.old_fw_unreleased', { v: dev.firmware, min: TTL_DUT_MIN_FW })}
          </div>
        )}
      </div>
    );
  }
  const linked = ttlLinked(dev, tt, tree);
  const rx = rxState(tt.rxAt, dev.presence);
  const rxMs = tt.rxAt ? Date.parse(tt.rxAt) : NaN;
  const full = Number.isFinite(rxMs) ? stamp(rxMs, { tz }) : '';
  const when = Number.isFinite(rxMs) ? relative(t, rxMs) : '';
  return (
    <div className="nuart" data-testid="nuart" data-nuart={linked ? 'linked' : 'label'}>
      {linked ? (
        <span className="nuart-line">
          <span className={`dot ${linked.presence}`} />
          <strong data-testid="nuart-name">{linked.name}</strong> · {t(`devices.${linked.presence}`)}{' '}
          <Link to={`/devices/${encodeURIComponent(linked.homeId)}/${encodeURIComponent(linked.aid)}`} style={{ marginLeft: 8 }} data-testid="nuart-open">
            {t('nuart.open')}
          </Link>
        </span>
      ) : (
        <strong data-testid="nuart-name">{tt.label}</strong>
      )}
      {linked && tt.label !== linked.name && (
        <div className="muted small" data-testid="nuart-label">
          {t('nuart.label_was', { label: tt.label })}
        </div>
      )}
      {!linked && (
        <div className="muted small" data-testid="nuart-hidden">
          {t('nuart.not_visible')}
        </div>
      )}
      <div className={'small ' + (rx === 'quiet' ? 'warn-text' : 'muted')} data-testid="nuart-rx" data-rx={rx}>
        {rx === 'none' && t('nuart.rx_none')}
        {rx === 'recent' && <span title={full}>{Date.now() - rxMs < 45_000 ? t('nuart.rx_just_now') : t('nuart.rx_recent', { when })}</span>}
        {rx === 'quiet' && t('nuart.rx_quiet', { at: full })}
        {rx === 'node_offline' && <span title={full}>{t('nuart.rx_node_offline', { when })}</span>}
      </div>
    </div>
  );
}
